import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { Tenant, TenantConfigurationUi } from '../../types/tenant.type';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { TenantInfo } from '../../types/tenant-info';

export interface TenantQueue {
  id: string;
  label: string;
}

export type UiPathQueueItemPriority = 'High' | 'Normal' | 'Low';

export interface QueueItemInput {
  data: Record<string, unknown>;
  deferUntil?: string;
  // Blue Prism only
  priority?: number;
  tags?: string[];
  status?: string;
  // UiPath only
  uiPathPriority?: UiPathQueueItemPriority;
  reference?: string;
  dueDate?: string;
}

export interface AddQueueItemsResponse {
  // the id is null for UiPath, which does not return the ids of the created items
  createdItems: { id: string | null }[];
}

@Injectable({ providedIn: 'root' })
export class TenantsApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/tenants`;
  }

  async getTenants(): Promise<Tenant[]> {
    const result = await this.get<Tenant[]>('');
    if (result == null) {
      return [];
    }
    // Convert the result to the Tenant type using ApiConversionHelper
    result.forEach(tenant => ApiConversionHelper.convertTenant(tenant));
    return result;
  }

  async getTenantsList(): Promise<TenantInfo[]> {
    return await this.get<TenantInfo[]>('list');
  }

  async getTenantsConfigurationUi(): Promise<TenantConfigurationUi[]> {
    return await this.get<TenantConfigurationUi[]>('configuration-ui');
  }

  async getTenantConfigurationUi(tenantId: string): Promise<TenantConfigurationUi> {
    return await this.get<TenantConfigurationUi>(`configuration-ui/${tenantId}`);
  }

  async getNewTenant(): Promise<Tenant> {
    const tenant = await this.get<Tenant>('new');
    ApiConversionHelper.convertTenant(tenant);
    return tenant;
  }

  async createTenant(tenant: Tenant): Promise<string> {
    ApiConversionHelper.convertTenantForApi(tenant);
    return await this.postWithResult<Tenant, string>('', tenant);
  }

  async updateTenant(tenant: Tenant): Promise<void> {
    ApiConversionHelper.convertTenantForApi(tenant);
    return await this.put('', tenant);
  }

  async deleteTenant(tenantId: string): Promise<void> {
    return await this.delete(`${tenantId}`);
  }

  async requestUpdatePendingAndRunningSessions(tenantId: string): Promise<void> {
    await this.post(`${tenantId}/request-update-sessions`, {});
  }

  async requestUpdatePendingAndRunningItems(tenantId: string): Promise<void> {
    await this.post(`${tenantId}/request-update-items`, {});
  }

  async refreshPowerAutomateCloudProcessUrls(tenantId: string, token: string): Promise<void> {
    return await this.post(`${tenantId}/refresh-power-automate-cloud-process-urls`, { token });
  }

  async testRpaConnection(tenantId: string): Promise<void> {
    return await this.post(`${tenantId}/test-rpa-connection`, null);
  }

  async getQueues(tenantId: string): Promise<TenantQueue[]> {
    return (await this.get<TenantQueue[]>(`${tenantId}/queues`)) ?? [];
  }

  async addQueueItems(tenantId: string, queueId: string, items: QueueItemInput[]): Promise<AddQueueItemsResponse> {
    return await this.postWithResult<{ items: QueueItemInput[] }, AddQueueItemsResponse>(
      `${tenantId}/queues/${queueId}/items`,
      { items },
    );
  }

}
